(() => {
  const productJsonUrlFromHref = (href) => {
    const url = new URL(href, window.location.origin);
    // Shopify supports /products/<handle>.js
    url.search = '';
    url.hash = '';
    if (!url.pathname.endsWith('.js')) url.pathname = `${url.pathname.replace(/\/$/, '')}.js`;
    return url.toString();
  };

  const select = (root, selector) => root.querySelector(selector);

  const setActiveSwatch = (container, activeEl) => {
    container.querySelectorAll('.product-card-siblings__item').forEach((el) => el.classList.remove('product-card-siblings__item--active'));
    if (activeEl) activeEl.classList.add('product-card-siblings__item--active');
  };

  const getProductJsonCached = async (jsonUrl, { signal } = {}) => {
    if (cache.has(jsonUrl)) return cache.get(jsonUrl);

    const res = await fetch(jsonUrl, { headers: { Accept: 'application/json' }, signal });
    if (!res.ok) throw new Error(`Bad response: ${res.status}`);
    const product = await res.json();
    cache.set(jsonUrl, product);
    return product;
  };

  const preserveSearch = (fromHref, toHref) => {
    try {
      const from = new URL(fromHref, window.location.origin);
      const to = new URL(toHref, window.location.origin);
      to.search = from.search;
      return to.toString();
    } catch {
      return toHref;
    }
  };

  const swapPrimaryMediaToImage = (productBlock, imgSrc, title) => {
    if (!imgSrc) return;

    const primary = select(productBlock, '.product-block__image--primary');
    if (!primary) return;

    // If the primary media is a video/iframe, replace it entirely (otherwise the old video keeps playing)
    const hasRichMedia = !!primary.querySelector('video, iframe');

    // If there is already an img, just update it (fast path)
    const existingImg = primary.querySelector('img.theme-img, img');
    if (existingImg && !hasRichMedia) {
      existingImg.src = imgSrc;
      existingImg.alt = title || existingImg.alt || '';
      existingImg.removeAttribute('srcset');
      existingImg.removeAttribute('sizes');
      return;
    }

    // Otherwise (e.g. autoplay video), replace media with an image
    const imgAr = primary.querySelector('.img-ar');
    const img = document.createElement('img');
    img.className = 'theme-img';
    img.loading = 'eager';
    img.decoding = 'async';
    img.alt = title || '';
    img.src = imgSrc;
    img.style.width = '100%';
    img.style.height = '100%';
    img.style.objectFit = 'cover';
    img.style.display = 'block';

    primary.innerHTML = '';

    if (imgAr) {
      // Reuse the existing aspect-ratio wrapper to keep identical height/positioning.
      const wrap = imgAr.cloneNode(false);
      wrap.appendChild(img);
      primary.appendChild(wrap);
    } else {
      primary.appendChild(img);
    }
  };

  const blockState = new WeakMap();

  const rememberCurrentState = (productBlock) => {
    const currentHref = select(productBlock, '.product-link')?.getAttribute('href');
    if (!currentHref) return;

    const primary = select(productBlock, '.product-block__image--primary');
    if (!primary) return;

    const title = select(productBlock, '.product-block__title')?.textContent || '';

    let state = blockState.get(productBlock);
    if (!state) {
      state = { mediaByHref: new Map(), titleByHref: new Map() };
      blockState.set(productBlock, state);
    }

    if (!state.mediaByHref.has(currentHref)) state.mediaByHref.set(currentHref, primary.innerHTML);
    if (!state.titleByHref.has(currentHref)) state.titleByHref.set(currentHref, title);
  };

  const restoreStateIfKnown = (productBlock, targetHref) => {
    const state = blockState.get(productBlock);
    if (!state) return false;

    const primary = select(productBlock, '.product-block__image--primary');
    if (!primary) return false;

    const html = state.mediaByHref.get(targetHref);
    if (!html) return false;

    primary.innerHTML = html;

    const title = state.titleByHref.get(targetHref);
    if (title) {
      const titleEl = select(productBlock, '.product-block__title');
      if (titleEl) titleEl.textContent = title;
    }

    return true;
  };

  const updateCardFromProduct = (productBlock, product) => {
    if (!productBlock || !product) return;

    productBlock.dataset.productId = product.id;
    productBlock.setAttribute('data-product-id', product.id);

    productBlock.querySelectorAll('.product-link, .quickbuy-toggle').forEach((a) => {
      a.href = preserveSearch(a.href, product.url);
    });

    const titleEl = select(productBlock, '.product-block__title');
    if (titleEl) titleEl.textContent = product.title;

    const targetHref = product.url;
    if (!restoreStateIfKnown(productBlock, targetHref)) {
      swapPrimaryMediaToImage(productBlock, product.featured_image, product.title);
    }
  };

  const cache = new Map();
  const inflight = new Map();

  const prefetchForLink = (swatchLink) => {
    const href = swatchLink?.getAttribute?.('href');
    if (!href) return;
    const jsonUrl = productJsonUrlFromHref(href);
    if (cache.has(jsonUrl) || inflight.has(jsonUrl)) return;

    const controller = new AbortController();
    const p = getProductJsonCached(jsonUrl, { signal: controller.signal })
      .catch(() => {}) // silent prefetch
      .finally(() => inflight.delete(jsonUrl));

    inflight.set(jsonUrl, { p, controller });

    // Don't let prefetches linger too long on slow connections
    window.setTimeout(() => {
      const entry = inflight.get(jsonUrl);
      if (entry) entry.controller.abort();
    }, 5000);
  };

  // Prefetch on hover/focus so click is instant
  document.addEventListener('mouseenter', (evt) => {
    const swatchLink = evt.target.closest?.('a.product-card-siblings__item');
    if (swatchLink) prefetchForLink(swatchLink);
  }, true);

  document.addEventListener('focusin', (evt) => {
    const swatchLink = evt.target.closest?.('a.product-card-siblings__item');
    if (swatchLink) prefetchForLink(swatchLink);
  });

  document.addEventListener('click', async (evt) => {
    const swatchLink = evt.target.closest('a.product-card-siblings__item');
    if (!swatchLink) return;

    const productBlock = swatchLink.closest('product-block');
    if (!productBlock) return;

    // Respect new-tab/window intent
    if (evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey || evt.button !== 0) return;

    evt.preventDefault();

    rememberCurrentState(productBlock);

    const siblingsContainer = swatchLink.closest('.product-card-siblings');
    if (siblingsContainer) setActiveSwatch(siblingsContainer, swatchLink);

    const href = swatchLink.getAttribute('href');
    if (!href) return;

    const jsonUrl = productJsonUrlFromHref(href);

    try {
      productBlock.classList.add('is-loading');

      let product = cache.get(jsonUrl);
      if (!product) product = await getProductJsonCached(jsonUrl);

      updateCardFromProduct(productBlock, product);
    } catch (e) {
      // If anything fails, fall back to normal navigation.
      window.location.href = href;
    } finally {
      productBlock.classList.remove('is-loading');
    }
  });
})();

